import { useTranslation } from 'react-i18next'
import { Shield, Eye, Zap, Check, X } from 'lucide-react'
import type { BrandAnalysis as BrandData } from '@/lib/api/amazon-listing'
import { SectionCard } from '@/components/v2/tools/amazon-listing/shared/SectionCard'

const COLORS = {
  high: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
} as const
const POS = {
  front: { border: 'border-l-green-500', key: 'brandPositionFront' },
  middle: { border: 'border-l-yellow-500', key: 'brandPositionMiddle' },
  end: { border: 'border-l-orange-500', key: 'brandPositionEnd' },
} as const
const LVL = { high: 'brandAwarenessHigh', medium: 'brandAwarenessMedium', low: 'brandAwarenessLow' } as const
const Bool = ({ v }: { v: boolean }) =>
  v ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-400" />

export function BrandAnalysis({ data }: { data: BrandData }) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.report.${key}`)
  const pos = POS[data.title_recommendation.position]
  const s = data.awareness_signals

  return (
    <SectionCard title={r('brandAnalysis')}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-neutral-400" />
          <span className="text-lg font-bold text-neutral-900 dark:text-white">{data.brand_name}</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${COLORS[data.awareness_level]}`}>
            {r(LVL[data.awareness_level])}
          </span>
        </div>

        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-2">
            <Eye className="h-3.5 w-3.5" /> {r('brandSignals')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label: r('brandWikipedia'), content: <Bool v={s.has_wikipedia} /> },
              { label: r('brandWebsite'), content: <Bool v={s.has_official_website} /> },
              { label: r('brandRegistry'), content: <Bool v={s.brand_registry} /> },
              { label: r('brandSearchVolume'), content: <span className="text-sm">{s.search_volume_indicator}</span> },
              { label: r('brandMarketplace'), content: <span className="text-sm">{s.marketplace_presence}</span> },
            ].map(({ label, content }) => (
              <div key={label} className="flex items-center justify-between rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-2">
                <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>
                {content}
              </div>
            ))}
          </div>
        </div>

        <div className={`border-l-4 ${pos.border} bg-neutral-50 dark:bg-neutral-700/30 p-4 rounded-r-lg`}>
          <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-1">
            <Zap className="h-3.5 w-3.5" /> {r('brandTitlePosition')}
          </p>
          <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{r(pos.key)}</p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">{data.title_recommendation.reasoning}</p>
        </div>

        {data.competitor_brand_landscape.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-2">{r('brandCompetitorLandscape')}</p>
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {data.competitor_brand_landscape.map((c) => (
                    <tr key={c.brand} className="border-b last:border-b-0 border-neutral-200 dark:border-neutral-700">
                      <td className="px-3 py-2 text-neutral-800 dark:text-neutral-200">{c.brand}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLORS[c.awareness as keyof typeof COLORS] ?? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'}`}>
                          {c.awareness}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-sm text-neutral-700 dark:text-neutral-300 italic">{data.recommendation}</p>
      </div>
    </SectionCard>
  )
}
